import { clsx } from 'clsx';

interface ProgressBarProps {
  value: number;           // 0–100
  max?: number;
  label?: string;
  showPercent?: boolean;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
};

export function ProgressBar({
  value,
  max = 100,
  label,
  showPercent = false,
  size = 'md',
  animated = true,
  className,
}: ProgressBarProps) {
  const percent = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className={clsx('w-full', className)}>
      {(label || showPercent) && (
        <div className="flex justify-between items-center mb-1.5">
          {label && (
            <span className="text-label-lg font-semibold text-[#3c494e]">{label}</span>
          )}
          {showPercent && (
            <span className="text-label-sm font-bold text-[#00677f]">{Math.round(percent)}%</span>
          )}
        </div>
      )}
      {/* Track */}
      <div className={clsx(
        'w-full rounded-full bg-[#e1e3e4] overflow-hidden',
        sizeClasses[size]
      )}>
        {/* Fill — cyan to bio-lime gradient */}
        <div
          className={clsx(
            'h-full rounded-full progress-fill',
            animated && 'transition-all duration-700 ease-out'
          )}
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
        />
      </div>
    </div>
  );
}

// ── XP Progress Bar (with level display) ────────────────────────────────────

interface XPBarProps {
  currentXP: number;
  levelXP: number;       // XP needed for next level
  level: number;
  className?: string;
}

export function XPBar({ currentXP, levelXP, level, className }: XPBarProps) {
  return (
    <div className={clsx('flex items-center gap-3', className)}>
      {/* Level badge */}
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#00677f] flex items-center justify-center">
        <span className="text-white text-label-sm font-bold">{level}</span>
      </div>
      {/* Bar + XP */}
      <div className="flex-1">
        <ProgressBar
          value={currentXP}
          max={levelXP}
          size="sm"
          showPercent={false}
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-label-sm text-[#6c797f]">{currentXP.toLocaleString()} XP</span>
          <span className="text-label-sm text-[#6c797f]">{levelXP.toLocaleString()} XP</span>
        </div>
      </div>
    </div>
  );
}
